import React from "react";
import { Plug, BookOpen, Newspaper, Building2, FileText } from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section, Card, Badge } from "../../components/ui/Primitives";
import { PageHero, CTASection } from "../../components/marketing/Shared";

// Lightweight content pages for secondary routes (integrations, docs, blog,
// about, legal). Real copy would live in a CMS; here we show a coherent shell.
const PAGES = {
  integrations: {
    icon: Plug,
    breadcrumb: "nav.integrations",
    title: "Integrations",
    lead: "Connect SecFace to the systems that own your signup and login — with a redirect and a couple of REST calls.",
    groups: [
      { title: "Redirect / callback", items: ["OAuth-style redirect", "redirect_uri + state", "status=passed|failed"] },
      { title: "Backend & auth", items: ["REST API", "Basic client_id:client_secret", "X-Api-Token", "One-time code exchange"] },
      { title: "Result", items: ["One-time code", "Server-to-server fetch", "Verdict snapshot"] },
      { title: "Client systems", items: ["SecLiveness", "Custom apps", "Per-tenant Client Apps"] },
      { title: "Deep analysis", items: ["insightface (SCRFD / ArcFace)", "MediaPipe", "Ollama vision (qwen2.5vl)"] },
    ],
  },
  docs: {
    icon: BookOpen,
    breadcrumb: "nav.docs",
    title: "Documentation & API",
    lead: "Quickstarts and REST reference for the redirect flow, result exchange and the biometric registry.",
    groups: [
      { title: "Getting started", items: ["Quickstart", "Authentication", "Sessions & verdicts"] },
      { title: "Liveness API", items: ["POST /liveness/start", "GET /liveness/session/{token}", "POST /liveness/result", "GET /v/{token}"] },
      { title: "Biometric API", items: ["POST /biometric/people/add", "POST /biometric/people/{id}/add_photo"] },
      { title: "Results", items: ["POST /liveness/result", "Verdict snapshot", "Images (base64)"] },
    ],
  },
  blog: {
    icon: Newspaper,
    breadcrumb: "nav.blog",
    title: "Blog & Resources",
    lead: "Articles and notes on liveness, anti-spoofing and integrating identity checks.",
    groups: [
      { title: "Latest", items: ["How liveness beats deepfakes", "Passive vs. active liveness", "Why a one-time code, not a JWT in the URL"] },
    ],
  },
  about: {
    icon: Building2,
    breadcrumb: "footer.about",
    title: "About SecFace",
    lead: "A sec4us company building facial liveness verification as a service — embeddable by any system through a simple redirect.",
    groups: [{ title: "Company", items: ["Mission", "Team", "Careers", "Press"] }],
  },
  privacy: { icon: FileText, breadcrumb: "footer.privacy", title: "Privacy Policy", lead: "How we collect, process and protect biometric and personal data.", legal: true },
  terms: { icon: FileText, breadcrumb: "footer.terms", title: "Terms of Service", lead: "The terms governing your use of SecFace.", legal: true },
  dpa: { icon: FileText, breadcrumb: "footer.dpa", title: "Data Processing Agreement", lead: "Roles, sub-processors, biometric-data handling and data-subject rights.", legal: true },
};

export default function SimplePage({ pageKey }) {
  const { t } = useI18n();
  const page = PAGES[pageKey] || PAGES.about;

  return (
    <>
      <PageHero breadcrumb={t(page.breadcrumb)} eyebrow={t(page.breadcrumb)} title={page.title} lead={page.lead} />
      <Section>
        <Container>
          {page.legal ? (
            <Card className="mx-auto max-w-3xl p-8">
              <div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
                <p>This is a demonstration document for the SecFace site build. In production, the full legal text would be maintained by legal counsel and versioned.</p>
                <p>Users always receive a clear consent and recording notice on the hosted verification page. Biometric data is minimized and the biometric result is never carried in the browser redirect — only a short-lived, single-use code.</p>
                <p>You control retention windows, data export and right-to-erasure requests. For a signed copy or custom terms, contact our team.</p>
              </div>
            </Card>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {page.groups.map((g) => (
                <Card key={g.title} className="p-6">
                  <h3 className="text-base font-bold">{g.title}</h3>
                  <ul className="mt-4 flex flex-wrap gap-2">
                    {g.items.map((it) => (
                      <li key={it}>
                        <Badge tone="neutral">{it}</Badge>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          )}
        </Container>
      </Section>
      <CTASection />
    </>
  );
}
